<template>
    <div v-if="!showOnlyFrame" :data-state="playerState" class="youtube-player">
        <div :class="{ fixed: fixed, fullscreened: fullscreened, mobile: mobile }" class="youtube-player-container">
            <PlayerControlsWrapper
                ref="controls"
                @toggle:mute="toggleMute"
                @toggle:fs="toggleFullScreen"
                @toggle:pip="togglePip"
                @change:volume="setVolume($event * 100)"
                @change:quality="setQuality($event)"
                @change:rate="setRate($event)"
                @update:seek="updateSeek($event)"
                @forwards="forwards"
                @backwards="backwards"
                @play="playVideo"
                @pause="pauseVideo"
                :quality="getQuality()"
                :componentId="componentId"
                :rate="getRate()"
                :quality-levels="getQualityLevels()"
                :rate-levels="geRateLevels()"
                :player-state="paused ? PLAYER_STATES.UNINIT : playerState"
                :preview="
                    playerState === PLAYER_STATES.PAUSED && !isShowPreviewOnPause
                        ? ''
                        : preview || `http://img.youtube.com/vi/${extractYoutubeKey(this.link)}/maxresdefault.jpg`
                "
                :muted="muted"
                :time="time"
                :duration="duration"
                :volume="volume / 100"
                :fullscreened="fullscreened"
            />
            <div class="youtube-player-video-container" :class="{ vertical: isVertical }">
                <div
                    class="frame-container"
                    :style="{ pointerEvents: playerState === PLAYER_STATES.UNSTARTED && false ? 'all' : 'none' }"
                >
                    <div class="player" :id="componentId"></div>
                </div>
            </div>
        </div>
    </div>
    <iframe
        style="aspect-ratio: 16 / 9"
        v-else
        width="100%"
        height="auto"
        :src="`https://www.youtube.com/embed/${extractYoutubeKey(link)}?autoplay=1`"
        title="YouTube video player"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
    ></iframe>
</template>

<script>
import PlayerControlsWrapper, { PLAYER_STATES } from "@components/Players/components/PlayerControlsWrapper.vue"
import BugIcon from "@icons/BugIcon.vue"
import { EventBus, EventsList } from "~events"

// --- Start of robust script loader logic ---
// This ensures the YouTube IFrame API is loaded only once, and any number of
// player components on the same page can be initialized correctly without race conditions.
if (typeof window.onYouTubeIframeAPIReadyCallbacks === "undefined") {
    window.onYouTubeIframeAPIReadyCallbacks = []
    window.youTubeApiLoaded = false

    window.onYouTubeIframeAPIReady = function () {
        window.youTubeApiLoaded = true
        window.onYouTubeIframeAPIReadyCallbacks.forEach(callback => {
            if (typeof callback === "function") {
                callback()
            }
        })
        window.onYouTubeIframeAPIReadyCallbacks = [] // Clear after execution
    }
}
// --- End of robust script loader logic ---

/**
 * @name YouTubeVideoPlayerContent
 * @description A custom YouTube video player component that uses the YouTube IFrame Player API
 * to provide a custom UI and enhanced controls. It handles player state, custom controls,
 * fullscreen mode, and robust API loading.
 */
export default {
    components: { PlayerControlsWrapper },
    props: {
        isShowPreviewOnPause: {
            type: Boolean,
            default: false,
            required: false
        },
        infoUnitId: {
            type: Number,
            default: 0
        },
        isVertical: {
            type: Boolean,
            default: false,
            required: false
        },
        link: {
            type: String,
            default: ""
        },
        // Optional host for the YouTube player (e.g., 'https://www.youtube-nocookie.com').
        host: {
            type: String,
            default: ""
        },
        preview: {
            type: String,
            default: ""
        },
        // Timeout in milliseconds for the YouTube IFrame API to load.
        apiTimeout: {
            type: Number,
            default: 10000
        }
    },
    name: "YouTubeVideoPlayerContent",
    data() {
        return {
            player: null, // The YouTube player instance.
            volume: 100,
            // Fallback "fixed" fullscreen mode for browsers that don't support the Fullscreen API.
            fixed: false,
            muted: false,
            fullscreened: false,
            mobile: false,
            playerState: PLAYER_STATES.UNINIT,
            PLAYER_STATES: PLAYER_STATES, // Enum of possible player states.
            duration: 0,
            time: 0,
            rate: 1,
            quality: "unknown",
            // A flag used for showing a loader animation on state change to improve perceived performance.
            paused: false,
            componentId: null, // A unique ID for the player's DOM element.
            showOnlyFrame: false,
            timeUpdateInterval: null,
            apiLoadTimeout: null,
            // Flag to prevent async operations on a destroyed component.
            isDestroyed: false
        }
    },

    /**
     * Called when the component is mounted.
     * Sets up the component, detects mobile/fullscreen capabilities, and initiates player loading.
     */
    mounted() {
        this.componentId = "ytb-id-" + this._uid
        this.mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 600

        this.fullScreenAvailable =
            document.fullscreenEnabled ||
            document.mozFullscreenEnabled ||
            document.webkitFullscreenEnabled ||
            document.msFullscreenEnabled

        if (this.fullScreenAvailable && !this.isIOS()) {
            const exitHandler = () => {
                if (!document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
                    setTimeout(() => {
                        if (this.fullscreened) {
                            this.fullscreened = false
                        }
                    }, 100)
                }
            }
            document.addEventListener("fullscreenchange", exitHandler, false)
            document.addEventListener("mozfullscreenchange", exitHandler, false)
            document.addEventListener("MSFullscreenChange", exitHandler, false)
            document.addEventListener("webkitfullscreenchange", exitHandler, false)
        }

        this.initScript()
    },

    /**
     * Called just before the component is destroyed.
     * Cleans up intervals and destroys the YouTube player instance to prevent memory leaks.
     */
    beforeDestroy() {
        this.isDestroyed = true
        if (this.apiLoadTimeout) {
            clearTimeout(this.apiLoadTimeout)
        }
        // Clear interval on unmount
        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval)
        }

        // Destroy the player
        if (this.player && typeof this.player.destroy === "function") {
            try {
                this.player.destroy()
            } catch (error) {
                console.warn("Error destroying YouTube player:", error)
            }
        }
    },
    methods: {
        getQualityLevels() {
            if (this.player && this.player.getAvailableQualityLevels) {
                return this.player.getAvailableQualityLevels()
            }
            return []
        },

        geRateLevels() {
            if (this.player && this.player.getAvailablePlaybackRates) {
                return this.player.getAvailablePlaybackRates()
            }
            return []
        },

        getQuality() {
            return this.quality || "unknown"
        },

        getRate() {
            return this.rate || 1
        },

        pauseVideo() {
            this.player.pauseVideo()
        },

        backwards() {
            this.updateSeek(this.player.getCurrentTime() - 10)
        },

        forwards() {
            this.updateSeek(this.player.getCurrentTime() + 10)
        },

        playVideo() {
            if (!this.player || typeof this.player.playVideo !== "function") {
                console.error("YouTube player is not ready yet")
                return
            }

            // Play only if the player is not already playing or buffering to prevent weird states.
            if ([PLAYER_STATES.PLAYING, PLAYER_STATES.BUFFERING].indexOf(this.playerState) === -1) {
                this.player.playVideo()
                window["activePlayer"] = this.componentId
            } else {
                console.warn(`playVideo() called in a blocked state: ${this.playerState}. Ignoring call.`)
            }
        },

        /**
         * YouTube API callback for when the player is ready.
         */
        onPlayerReady(event) {
            if (this.player && typeof this.player.getDuration === "function") {
                this.duration = this.player.getDuration()
            }

            // Restore volume from local storage.
            if (localStorage.getItem("yt-volume")) {
                const value = localStorage.getItem("yt-volume")
                this.volume = value
                this.player.setVolume(value)
            }

            this.setPlayerState(PLAYER_STATES.UNSTARTED)
            this.playVideo()
            this.$emit("ready", this.player)
        },

        /**
         * Updates the internal player state and emits events.
         * @param {number} state - The new player state from the YouTube API.
         */
        setPlayerState(state) {
            const setLoader = () => {
                this.paused = true
                setTimeout(() => {
                    this.paused = false
                }, 400)
            }

            if (state === PLAYER_STATES.PLAYING) {
                this.time = this.player.getCurrentTime()

                // Attempt to disable captions/subtitles.
                try {
                    this.player.unloadModule("captions")
                    this.player.unloadModule("cc")
                } catch (e) {
                    // This can fail if the module isn't loaded, which is fine.
                    // console.warn(e)
                }
            }

            if (this.playerState === PLAYER_STATES.PAUSED) {
                setLoader()
            }

            if (state === PLAYER_STATES.PAUSED) {
                this.$emit("pause")
            }

            if (state === PLAYER_STATES.UNSTARTED) {
                setLoader()
            }

            if (state === PLAYER_STATES.ENDED) {
                this.$emit("ended")
            }

            if (this.playerState === PLAYER_STATES.UNSTARTED) {
                this.$emit("play")
                setLoader()
            }

            this.$set(this, "playerState", state)
            this.update()
        },

        updateSeek(val) {
            this.time = val
            this.player.seekTo(val)
        },

        setQuality(val) {
            if (this.player) {
                this.quality = val
                this.player.setPlaybackQuality(val)
            }
        },

        onPlaybackRateChange(val) {
            this.rate = val.data
        },

        onPlaybackQualityChange(val) {
            this.quality = val.data
        },

        setRate(val) {
            if (this.player) {
                this.player.setPlaybackRate(val)
            }
        },

        setVolume(value) {
            if (this.player.isMuted()) {
                this.player.unMute()
                this.muted = false
            }

            this.player.setVolume(value)
            localStorage.setItem("yt-volume", value)
            this.volume = value
        },

        toggleMute() {
            if (this.player.isMuted()) {
                this.player.unMute()
                this.player.setVolume(50)
                this.volume = 50
                this.muted = false
            } else {
                this.player.mute()
                this.player.setVolume(0)
                this.volume = 0
                this.muted = true
            }
        },

        isInFullScreen() {
            return (
                (document.fullscreenElement && true) ||
                (document.webkitFullscreenElement && true) ||
                (document.mozFullScreenElement && true) ||
                (document.msFullscreenElement && true)
            )
        },

        togglePip() {
            const video = this.$el.querySelector(".youtube-player-container iframe")

            if (document.pictureInPictureElement) {
                document.exitPictureInPicture()
            } else if (document.pictureInPictureEnabled) {
                video.requestPictureInPicture()
            }
        },

        /**
         * Toggles fullscreen mode for the player.
         * Handles both native browser fullscreen and a fallback "fixed" mode.
         */
        toggleFullScreen() {
            const video = this.$el.querySelector(".youtube-player-container")

            this.fullscreened = !this.fullscreened

            if (this.fullscreened) {
                this.scrollTop = document.body.scrollTop
            } else {
                setTimeout(() => {
                    document.body.scrollTop = this.scrollTop
                }, 200)
            }

            // Fallback for browsers without native fullscreen support.
            if (!this.fullScreenAvailable) {
                this.fixed = !this.fixed
                return
            }

            // Native fullscreen handling.
            if (!this.isInFullScreen()) {
                if (video.requestFullScreen) {
                    video.requestFullScreen()
                } else if (video.mozRequestFullScreen) {
                    video.mozRequestFullScreen()
                } else if (video.webkitRequestFullScreen) {
                    video.webkitRequestFullScreen()
                } else if (video.webkitRequestFullscreen) {
                    video.webkitRequestFullscreen()
                } else if (video.webkitEnterFullscreen) {
                    video.webkitEnterFullScreen()
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen()
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen()
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen()
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen()
                }
            }
        },

        /**
         * Manages the loading of the YouTube IFrame API script.
         * This method ensures the API script is loaded only once, even if multiple
         * player components are on the same page. It uses a global callback queue
         * (`window.onYouTubeIframeAPIReadyCallbacks`) to collect initialization functions
         * from each player instance.
         *
         * When the YouTube API is ready, it calls the global `onYouTubeIframeAPIReady` function,
         * which then executes all queued callbacks.
         *
         * A critical part of this process is wrapping the `initPlayer` call in `this.$nextTick()`.
         * This is necessary to solve a race condition where the API might be ready before
         * Vue has finished rendering the player's target `<div>` in the DOM. `$nextTick`
         * defers the `initPlayer` execution until after the DOM has been updated, guaranteeing
         * that the target element exists.
         *
         * @see {@link https://developers.google.com/youtube/iframe_api_reference} for official API documentation.
         */
        initScript() {
            const init = () => {
                if (this.apiLoadTimeout) {
                    clearTimeout(this.apiLoadTimeout)
                }
                // Always wait for the next DOM tick to ensure the element exists
                this.$nextTick(() => {
                    this.initPlayer()
                })
            }

            if (window.youTubeApiLoaded) {
                init()
            } else {
                this.apiLoadTimeout = setTimeout(() => {
                    if (!window.youTubeApiLoaded && !this.isDestroyed) {
                        console.error("YouTube IFrame API failed to load in time.")
                        this.$emit("showDefault")
                    }
                }, this.apiTimeout)

                window.onYouTubeIframeAPIReadyCallbacks.push(init)

                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement("script")
                    tag.src = "https://www.youtube.com/iframe_api"

                    tag.onerror = () => {
                        console.error(
                            "Failed to load YouTube IFrame API script. Possibly blocked by an extension (e.g., AdBlock) or a network issue."
                        )
                        if (this.apiLoadTimeout) {
                            clearTimeout(this.apiLoadTimeout)
                        }
                        if (!this.isDestroyed) {
                            this.$emit("showDefault")
                        }
                    }

                    const firstScriptTag = document.getElementsByTagName("script")[0]
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
                }
            }
        },

        seek() {
            return 0
        },

        isIOS() {
            return (
                ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
                    navigator.platform
                ) ||
                // iPad on iOS 13 detection
                (navigator.userAgent.includes("Mac") && "ontouchend" in document)
            )
        },

        /**
         * Initializes the YT.Player instance once the API is ready and the component is mounted.
         */
        initPlayer() {
            if (this.isDestroyed) {
                return
            }

            if (typeof window.YT === "undefined" || typeof window.YT.Player === "undefined") {
                console.error("YT.Player is not available! initPlayer was called prematurely.")
                this.$emit("showDefault")
                return
            }

            const videoId = this.extractYoutubeKey(this.link)

            if (!document.getElementById(this.componentId)) {
                console.error(`Element with id "${this.componentId}" not found in DOM for player.`)
                this.$emit("showDefault")
                return
            }

            /**
             * Player variables to configure the YouTube player.
             * These parameters control the player's appearance and behavior.
             * @see {@link https://developers.google.com/youtube/player_parameters#Parameters} for a complete list of parameters.
             */
            const playerVars = {
                rel: 0, // Do not show related videos at the end.
                showinfo: 0, // Deprecated, but good to have for older players. Hides video title and uploader.
                modestbranding: 1, // Reduce the YouTube logo in the control bar.
                iv_load_policy: 3, // Do not show video annotations.
                muted: 0, // Player is not muted by default.
                enablejsapi: 1, // Enable the JavaScript API. Required for this component.
                origin: window.location.origin, // Security measure for the JS API.
                playsinline: 1, // Allow inline playback on iOS.
                autoplay: 0, // Do not autoplay the video on load.
                disablekb: 0, // Keep default keyboard controls enabled.
                controls: 0, // Hide the default YouTube controls, as we use custom ones.
                fs: 1, // Technically enables the fullscreen button, but our `controls: 0` hides it.
                start: this.extractStart(this.link) // Start video at a specific time if provided in URL.
            }

            this.player = new window.YT.Player(this.componentId, {
                height: window.innerHeight,
                width: window.innerWidth,
                videoId: videoId,
                playerVars: playerVars,
                host: this.host ? this.host : "https://www.youtube-nocookie.com",
                events: {
                    onPlaybackQualityChange: this.onPlaybackQualityChange.bind(this),
                    onPlaybackRateChange: this.onPlaybackRateChange.bind(this),
                    onReady: this.onPlayerReady.bind(this),
                    onError: this.onPlayerError.bind(this),
                    onStateChange: this.onPlayerStateChange.bind(this)
                }
            })

            EventBus.$on(EventsList.VIDEO_SET_SEEK, ({ unitId, seekTo }) => {
                if (this.infoUnitId === unitId) {
                    this.updateSeek(seekTo)
                }
            })
        },

        /**
         * YouTube API callback for player errors.
         */
        onPlayerError(event) {
            console.error(`YouTube Player Error for ${this.componentId}. Code: ${event.data}`, event)

            this.$emit("showDefault")

            const noti = text => {
                if (this.$notify) {
                    this.$notify({
                        position: "top-right",
                        color: "danger",
                        time: 5000,
                        title: this.t("expert.something_went_wrong"),
                        text: text,
                        icon: BugIcon
                    })
                }
            }
            switch (event.data) {
                case 2: {
                    noti("The request contains an invalid parameter value")
                    break
                }
                case 5: {
                    noti(
                        "The requested content cannot be played in an HTML5 player or another error related to the player has occurred."
                    )
                    fetch("https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js", {
                        method: "HEAD",
                        mode: "no-cors",
                        cache: "no-store"
                    })
                        .then(() => {})
                        .catch(() => {
                            noti("Please disable Adblock")
                        })
                    break
                }
                case 100: {
                    noti("The video requested was not found.")
                    break
                }
                case 101:
                case 150: {
                    noti("The owner of the requested video does not allow it to be played in embedded players")
                    break
                }
                default: {
                    console.error(event)
                }
            }
        },

        /**
         * YouTube API callback for player state changes. Manages the time update interval.
         * @param {object} event - The state change event object.
         */
        onPlayerStateChange(event) {
            // Manage interval based on player state
            if (event.data === PLAYER_STATES.PLAYING) {
                // Start interval only when playing begins
                if (!this.timeUpdateInterval) {
                    this.timeUpdateInterval = setInterval(() => {
                        if (this.player && typeof this.player.getCurrentTime === "function") {
                            this.time = this.player.getCurrentTime()

                            if (typeof this.player.getDuration === "function") {
                                this.$emit("timeupdate", {
                                    seconds: this.player.getCurrentTime(),
                                    duration: this.player.getDuration()
                                })
                            }
                        }
                    }, 1000)
                }
            } else {
                // Clear interval on pause, end, etc.
                if (this.timeUpdateInterval) {
                    clearInterval(this.timeUpdateInterval)
                    this.timeUpdateInterval = null
                }
            }

            this.setPlayerState(event.data)
        },

        update() {
            this.didUpdate = true
            this.$nextTick(() => {
                this.didUpdate = false
            })
        },

        extractYoutubeKey(url) {
            const regExp =
                /^.*(?:(?:youtu\.be\/|v\/|vi\/|live\/|u\/\w\/|embed|shorts|\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/
            let match = url.match(regExp)
            let videoId = null
            if (match) {
                if (match[7] && match[7].length === 11) {
                    videoId = match[7]
                } else if (match[1] && match[1].length === 11) {
                    videoId = match[1]
                }
            }
            return videoId || false
        },

        extractStart(url_string) {
            try {
                let url = new URL(url_string)
                return parseInt(url.searchParams.get("t")) || 0
            } catch (e) {
                return 0
            }
        }
    },
    watch: {
        /**
         * Toggles a class on the body when the 'fixed' fullscreen mode changes.
         * This is a side-effect for styling purposes.
         */
        fixed(val) {
            if (val) {
                document.body.classList.add("body-black")
            } else {
                document.body.classList.remove("body-black")
            }
        }
    }
}
</script>



<style scoped lang="sass">
.youtube-player
    border-radius: 10px
    position: relative
    padding-bottom: 50%
    height: 0 !important
    .frame-container
        height: 100%
        width: 100%
        position: relative
        .black-loader
            position: absolute
            top: 0px
            bottom: 0
            left: 0
            right: 0
            width: 100%
            height: 100%
            z-index: 555
            background: #000000
            border-radius: 10px
    video
        width: 100%
        border-radius: 10px
    .youtube-player-video-container
        position: relative
        top: 0
        right: 0
        bottom: 0
        left: 0
        height: 100%
    .youtube-player-container
        position: absolute
        max-height: 100%
        height: 100%
        width: 100%
        border-radius: 10px
        overflow: hidden
        &.fullscreened
            border-radius: 0
            &::v-deep
                .controls-loader, .controls-play
                    border-radius: 0
        &.fixed
            position: fixed
            top: 0
            right: 0
            left: 0
            bottom: 0
            z-index: 9999999999
            background-color: #000
            display: flex
            align-items: center
            justify-content: center
            @media screen and (orientation: portrait)
                .frame-container
                    height: 100% !important
                    position: absolute
            @media screen and (orientation: landscape)
                .frame-container
                    height: auto
            .youtube-player-video-container
                height: max-content
                display: flex
                width: 100%
                &.vertical
                    height: 100% !important
                &::before
                    float: left
                    padding-top: 56.25%
                    content: ""
                &::after
                    display: block
                    content: ""
                    clear: both
            .actions
                border-radius: 0 !important

        &:fullscreen
            width: 100vw
            position: fixed
            .youtube-player-video-container
                @supports not (aspect-ratio: 16 / 9)
                    width: 100% !important
                    padding-top: 56.25%
                    top: 50% !important
                    position: relative
                    left: auto
                    right: auto
                    bottom: auto
                    height: auto
                    transform: translateY(-50%) !important
                    .frame-container
                        position: absolute !important
                        top: 0 !important
                        left: 0 !important
                        bottom: 0 !important
                        right: 0 !important
                @supports  (aspect-ratio: 16 / 9)
                    transform: translateY(-50%) !important
                    top: 50% !important
                    aspect-ratio: 16 / 9 !important
                    width: 100% !important
                    height: auto !important
                    max-height: 100% !important
                    &.vertical
                        height: 100% !important
            video
                height: 100%
                width: 100%
                border-radius: 0
                object-fit: contain
                @media (orientation: landscape)
                    object-fit: cover
            .actions
                border-radius: 0 !important

    &::v-deep
        video::-webkit-media-controls, video::-webkit-media-controls-enclosure
            display: none
            -webkit-appearance: none
    &::v-deep
        .player
            width: 300%
            height: 100%
            margin-left: -100%
            position: relative
            padding-top: 0
</style>
